import { isFailed } from '@payments-checkout/shared/helpers/constants/order-status.const';
import {
  DigitalWallets,
  DigitalWalletsStatusTypeAnaliticaEnum,
  StatusTypeAnaliticaEnum,
} from '@payments-checkout/shared/plugins/analitica/analitica.enum';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { reportError } from '@payments-checkout/shared/observability';
import {
  usePaymentMethod,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { useRouter } from 'next/navigation';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { formatAmount } from '../../helpers/formatAmount';
import { mapMerchantSession } from '../../helpers/mapMerchantSession';
import { pickApplePayVersion } from '../../helpers/applePaySdk';
import { errorMessagePaymentGeneric } from '../../../components/pages/cardAddress/errorMessage';
import { usePageSharedData } from '../../../hooks/usePageSharedData';
import { sendApplePayConfig } from '../useMerchantData';
import { useApplePayPayment } from '../useApplePayPayment';

declare global {
  interface Window {
    ApplePaySession: (version: number, data: unknown) => unknown;
  }
}

export const useApplePaySession = () => {
  const applePaySessionRef = useRef<any>(null);
  const merchantIdRef = useRef<string | null>(null);
  const { paymentLinkId, paymentLinkIdData, total, isRecurrence } =
    usePageSharedData();
  const { sessionId } = useSessionId();
  const applePayData = paymentLinkIdData?.data?.apple_payment_details;
  const { push } = useRouter();
  const { mutateAsync: sendPayment } = useApplePayPayment();
  const { setShowFailedPaymentAlert, setFailedPaymentMessage, installments } =
    usePaymentMethod();
  const addressForm = useFormContext();

  const sessionData = () => {
    return {
      applicationData: btoa(paymentLinkId),
      countryCode: applePayData?.country_code,
      currencyCode: applePayData?.currency_code,
      merchantCapabilities: applePayData?.merchant_capabilities,
      supportedNetworks: applePayData?.supported_networks,
      requiredBillingContactFields:
        applePayData?.required_billing_contact_fields,
      requiredShippingContactFields:
        applePayData?.required_shipping_contact_fields,
      shippingType: applePayData?.shipping_type,
      supportedCountries: applePayData?.supported_countries,
      shippingMethods: formatAmount(applePayData?.shipping_methods),
      lineItems: formatAmount(applePayData?.line_items),
      total: {
        label: applePayData?.total.label,
        amount: formatAmount(total?.amount?.toString()),
      },
    };
  };

  function onValidateMerchant() {
    sendApplePayConfig(paymentLinkId, sessionId)
      .then((response) => {
        merchantIdRef.current = response.merchant_identifier;
        const merchant = mapMerchantSession(response);
        applePaySessionRef.current.completeMerchantValidation(merchant);
      })
      .catch((error) => {
        reportError(error, {
          source: 'digital_wallet',
          wallet: 'apple_pay',
          stage: 'validate_merchant',
        });
        applePaySessionRef.current?.abort();
      });
  }

  function onPaymentAuthorized(event: any) {
    const paymentData = {
      version: event.payment.token.paymentData.version,
      merchant_identifier: merchantIdRef.current,
      transaction_id: event.payment.token.transactionIdentifier,
      ephemeral_public_key:
        event.payment.token.paymentData.header.ephemeralPublicKey,
      public_key_hash: event.payment.token.paymentData.header.publicKeyHash,
      signature: event.payment.token.paymentData.signature,
      data: event.payment.token.paymentData.data,
      installments: installments || 1,
    };
    sendPayment({ paymentData, appleDetails: event })
      .then(function (response) {
        if (!isFailed(response.order_status)) {
          completePayment('STATUS_SUCCESS');
          if (isRecurrence) {
            push(`/${paymentLinkId}/credit-card-success`);
          } else {
            push(`/status/${sessionId}`);
          }
          analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
            StatusTypeAnaliticaEnum.SUCCESS,
            DigitalWallets.APPLE_PAY
          );
        } else {
          setFailedPaymentMessage(errorMessagePaymentGeneric(1000));
          setShowFailedPaymentAlert(true);
          completePayment('STATUS_FAILURE');
          analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
            StatusTypeAnaliticaEnum.ERROR,
            DigitalWallets.APPLE_PAY
          );
        }
      })
      .catch(function (error) {
        reportError(error, {
          source: 'digital_wallet',
          wallet: 'apple_pay',
        });
        setFailedPaymentMessage(errorMessagePaymentGeneric(1000));
        setShowFailedPaymentAlert(true);
        completePayment('STATUS_FAILURE');
        analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
          StatusTypeAnaliticaEnum.ERROR,
          DigitalWallets.APPLE_PAY
        );
      });
  }

  function onCancel() {
    analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
      DigitalWalletsStatusTypeAnaliticaEnum.DESISTENCIA,
      DigitalWallets.APPLE_PAY
    );
  }

  const completePayment = (status: string) => {
    const authorizationResult = {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      status: ApplePaySession[status],
      errors: [],
    };
    applePaySessionRef.current?.completePayment(authorizationResult);
  };

  const createAppleSession = async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    if (!window.ApplePaySession) {
      reportError(new Error('ApplePaySession indisponível'), {
        source: 'digital_wallet',
        wallet: 'apple_pay',
        stage: 'begin_session',
      });
      return;
    }

    analiticaInstance.events.homePage.submitData('applepay');

    if (addressForm && !(await addressForm.trigger())) return;

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      const session = new ApplePaySession(pickApplePayVersion(), sessionData());
      applePaySessionRef.current = session;
      session.onvalidatemerchant = onValidateMerchant;
      session.onpaymentauthorized = onPaymentAuthorized;
      session.oncancel = onCancel;
      session.begin();
    } catch (error) {
      reportError(error, {
        source: 'digital_wallet',
        wallet: 'apple_pay',
        stage: 'create_session',
      });
    }
  };

  return { createAppleSession };
};
